import { Button, Grid, TextField } from '@material-ui/core';
import { load as exifLoad, ImageIFD } from 'piexifjs';
import React, { useCallback, useMemo, useState } from 'react';
import { Country, countryNames, IdTemplateType, idTemplateTypes } from './constants';
import { ReusableSelect as Select } from 'components/reusable/select';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { adminCreateTemplate } from 'store/sagas/sagas';
const resizeImage = async ({ img, ratio, width, height, type }) => {
    const elem = document.createElement('canvas');
    width *= ratio;
    height *= ratio;
    elem.width = width;
    elem.height = height;
    const ctx = elem.getContext('2d');
    if (!ctx) {
        throw new Error("Couldn't find context for canvas. Can't draw image");
    }
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise((resolve, reject) => {
        ctx.canvas.toBlob((b) => {
            if (!b) {
                reject(new Error('Failed to convert canvas to blob'));
                return;
            }
            resolve(b);
        }, type, 1);
    });
    return { blob, imgSrc: await blobToBase64(blob) };
};
const resizeImageCanvas = ({ img, max = 250 }) => {
    const ratio = img.width > img.height ? max / img.width : max / img.height;
    const width = ratio ? ratio * img.width : img.width;
    const height = ratio ? ratio * img.height : img.height;
    return { width, height };
};
const toExif = (imgSrc) => {
    try {
        return exifLoad(imgSrc);
    }
    catch (error) {
        console.error(error);
        return null;
    }
};
const blobToBase64 = async (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.addEventListener('abort', () => {
        reject(new Error('file reading was aborted'));
    }, { once: true });
    reader.addEventListener('error', () => {
        reject(new Error('file reading has failed'));
    }, { once: true });
    reader.addEventListener('loadend', () => {
        resolve(reader.result);
    }, { once: true });
});
const rotateImageByExif = async ({ imgSrc, type }) => new Promise((resolve) => {
    const exif = toExif(imgSrc);
    if (!exif) {
        resolve(imgSrc);
        return;
    }
    const image = new Image();
    image.src = imgSrc;
    image.addEventListener('load', () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        let x = 0;
        let y = 0;
        if (!ctx) {
            throw new Error("Couldn't find context for canvas. Can't rotate image");
        }
        ctx.save();
        if (exif['0th']) {
            const orientation = exif['0th'][ImageIFD.Orientation];
            // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
            switch (orientation) {
                case 2: {
                    x = -canvas.width;
                    ctx.scale(-1, 1);
                    break;
                }
                case 3: {
                    x = -canvas.width;
                    y = -canvas.height;
                    ctx.scale(-1, -1);
                    break;
                }
                case 4: {
                    y = -canvas.height;
                    ctx.scale(1, -1);
                    break;
                }
                case 5: {
                    canvas.width = image.height;
                    canvas.height = image.width;
                    ctx.translate(canvas.width, canvas.height / canvas.width);
                    ctx.rotate(Math.PI / 2);
                    y = -canvas.width;
                    ctx.scale(1, -1);
                    break;
                }
                case 6: {
                    canvas.width = image.height;
                    canvas.height = image.width;
                    ctx.translate(canvas.width, canvas.height / canvas.width);
                    ctx.rotate(Math.PI / 2);
                    break;
                }
                case 7: {
                    canvas.width = image.height;
                    canvas.height = image.width;
                    ctx.translate(canvas.width, canvas.height / canvas.width);
                    ctx.rotate(Math.PI / 2);
                    x = -canvas.height;
                    ctx.scale(-1, 1);
                    break;
                }
                case 8: {
                    canvas.width = image.height;
                    canvas.height = image.width;
                    ctx.translate(canvas.width, canvas.height / canvas.width);
                    ctx.rotate(Math.PI / 2);
                    x = -canvas.height;
                    y = -canvas.width;
                    ctx.scale(-1, -1);
                    break;
                }
                default:
                    // noop
                    break;
            }
        }
        ctx.drawImage(image, x, y);
        ctx.restore();
        resolve(canvas.toDataURL(type, 1));
    });
});
const convertFile = async ({ file, max }) => {
    const { type } = file;
    let imgSrc = await blobToBase64(file);
    // eslint-disable-next-line require-atomic-updates
    imgSrc = await rotateImageByExif({ imgSrc, type });
    const img = new Image();
    await new Promise((resolve, reject) => {
        img.addEventListener('error', ({ error }) => {
            reject(error);
        });
        img.addEventListener('load', resolve);
        img.src = imgSrc;
    });
    if (!img.width) {
        throw new Error('no image loaded');
    }
    const ratio = resizeRatio({
        width: img.width,
        height: img.height,
        max
    });
    let blob = file;
    if (ratio) {
        // eslint-disable-next-line require-atomic-updates
        ({ blob, imgSrc } = await resizeImage({
            file,
            img,
            width: img.width,
            height: img.height,
            type,
            ratio
        }));
    }
    const { width, height } = resizeImageCanvas({ img });
    return { file: blob, width, height, imgSrc };
};
const resizeRatio = ({ width, height, max }) => {
    if (width > max || height > max) {
        return width > height ? max / width : max / height;
    }
    return null;
};
export const TemplateCreateForm = () => {
    const { asyncDispatch, dispatch } = useGlobalState();
    const [type, setType] = useState(IdTemplateType.DriversLicense);
    const [country, setCountry] = useState(Country.US);
    const [idState, setIdState] = useState('');
    const [imgSrc, setImgSrc] = useState('');
    const [version, setVersion] = useState('');
    const [invalidData, setInvalidData] = useState(false);
    const createTemplate = () => {
        const data = {
            type,
            country,
            state: idState === '' ? null : idState,
            version,
            image: imgSrc
        };
        asyncDispatch(adminCreateTemplate(data))
            .then(() => {
            dispatch(setMessages({
                value: `The template has been created`,
                severity: 'success'
            }));
        })
            .catch(console.error);
    };
    const handleType = useCallback((e) => {
        setType(e.target.value);
        setInvalidData(false);
    }, []);
    const handleCountry = useCallback((e) => {
        setCountry(e.target.value);
        setInvalidData(false);
    }, []);
    const handleState = useCallback((e) => {
        setIdState(e.target.value);
        setInvalidData(false);
    }, []);
    const handleVersion = useCallback((e) => {
        setVersion(e.target.value);
        setInvalidData(false);
    }, []);
    const handlePhoto = useCallback((event) => {
        const file = event.currentTarget.files?.[0];
        if (!file) {
            return;
        }
        if (file.size > 10 * 1_048_576) {
            // eslint-disable-next-line no-alert
            alert('file too large');
            return;
        }
        convertFile({
            max: 1600,
            file
        }).then((r) => {
            setImgSrc(r.imgSrc);
        }, console.error);
    }, []);
    const idTemplateTypeOptions = useMemo(() => idTemplateTypes.map((v) => ({ name: v, id: v })), []);
    const countryOptions = useMemo(() => {
        const out = [];
        for (const id of Object.values(Country)) {
            out.push({ id, name: `${countryNames[id]} (${id})` });
        }
        return out;
    }, []);
    return (React.createElement("div", null,
        React.createElement("h1", null, "Create Template"),
        React.createElement("div", { className: "filters-container" },
            React.createElement(Grid, { container: true },
                React.createElement(Grid, { container: true, item: true, xs: 9 },
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(Select, { className: "g-full-width u-margin-bottom-small", isClearable: false, items: idTemplateTypeOptions, label: "Type", onChange: handleType, required: true, value: type })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(Select, { className: "g-full-width u-margin-bottom-small", isClearable: false, items: countryOptions, label: "Country", onChange: handleCountry, value: country })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", error: invalidData, label: "State", onChange: handleState, value: idState })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", error: invalidData, label: "Version", onChange: handleVersion, required: true, value: version }))),
                React.createElement(Grid, { container: true, item: true, xs: 3 },
                    React.createElement(Button, { component: "label", variant: "contained" },
                        "Upload File",
                        React.createElement("input", { accept: "image/jpeg, image/png", hidden: true, multiple: false, onChange: handlePhoto, type: "file" })))),
            React.createElement(Grid, { container: true },
                React.createElement(Button, { onClick: createTemplate }, "Create")))));
};
